import { Game } from './game/Game.js'
import { getGamepadMenuDirection } from './game/InputManager.js'
import { drawPixelSpriteToCanvas } from './rendering/PixelGrid.js'
import {
  HUD_APPLE_SPRITE,
  HUD_ARROW_SPRITE,
  HUD_BANANA_SPRITE,
  HUD_COIN_SPRITE,
  HUD_DRAGON_FRUIT_SPRITE,
  HUD_GOLDEN_PINEAPPLE_SPRITE,
  HUD_GOLDEN_KIWI_SPRITE,
  HUD_MOON_MELON_SPRITE,
  HUD_STAR_FRUIT_SPRITE,
  HUD_WATERMELON_SPRITE,
  HUD_HEART_SPRITE,
  HUD_POTION_SPRITE,
  HUD_SHIELD_SPRITE,
  HUD_SWORD_SPRITE
} from './rendering/UISprites.js'

const game = new Game()
const gameContainer = document.getElementById('game-container')

for (const eventName of ['selectstart', 'dragstart']) {
  gameContainer?.addEventListener(eventName, event => event.preventDefault())
}

gameContainer?.addEventListener('dblclick', event => event.preventDefault())
for (const eventName of ['gesturestart', 'gesturechange', 'gestureend']) {
  document.addEventListener(eventName, event => event.preventDefault(), {
    passive: false
  })
}

const shopSprites = {
  sword: HUD_SWORD_SPRITE,
  shield: HUD_SHIELD_SPRITE,
  arrow: HUD_ARROW_SPRITE,
  potion: HUD_POTION_SPRITE,
  apple: HUD_APPLE_SPRITE,
  banana: HUD_BANANA_SPRITE,
  dragonFruit: HUD_DRAGON_FRUIT_SPRITE,
  goldenPineapple: HUD_GOLDEN_PINEAPPLE_SPRITE,
  goldenKiwi: HUD_GOLDEN_KIWI_SPRITE,
  moonMelon: HUD_MOON_MELON_SPRITE,
  watermelon: HUD_WATERMELON_SPRITE,
  starFruit: HUD_STAR_FRUIT_SPRITE,
  heart: HUD_HEART_SPRITE,
  coin: HUD_COIN_SPRITE
}

for (const canvas of document.querySelectorAll('[data-shop-sprite]')) {
  drawPixelSpriteToCanvas(canvas, shopSprites[canvas.dataset.shopSprite])
}

function readStoredGame() {
  // Only an explicit pause-menu save creates a resumable title-screen game.
  // Background autosaves are recovery data, not player-selected save points.
  return game.saveManager.load()
}

function readDevelopmentRecovery() {
  if (!import.meta.env.DEV) return null
  try {
    const data = localStorage.getItem('poe-quest-autosave')
    return data ? JSON.parse(data) : null
  } catch {
    return null
  }
}

function clearStoredGames() {
  try {
    localStorage.removeItem('poe-quest-autosave')
  } catch {
    // A new game must remain playable when browser storage is unavailable.
  }
  game.saveManager.deleteSave()
}

let touchGuideShownThisPage = false

function showTouchGuideOnce() {
  const hasTouch = navigator.maxTouchPoints > 0 || matchMedia('(any-pointer: coarse)').matches
  if (!hasTouch || touchGuideShownThisPage) return
  try {
    if (sessionStorage.getItem('poesTouchGuideShown') === '1') return
    sessionStorage.setItem('poesTouchGuideShown', '1')
  } catch {
    // Fall back to page memory in private browsing or restricted embeds.
  }
  touchGuideShownThisPage = true
  setTimeout(() => game.showControlsGuide('touch'), 0)
}

// Local rebuilds resume the latest recovery point with one action. Published
// builds only expose saves that the player explicitly created.
const storedGame = readStoredGame()
const developmentRecovery = readDevelopmentRecovery()
const resumableGame = developmentRecovery || storedGame
if (resumableGame) {
  const continueButton = document.getElementById('continue-btn')
  const newGameButton = document.getElementById('new-game-btn')
  if (developmentRecovery) {
    continueButton.textContent = 'Resume'
    newGameButton.hidden = true
  }
  document.getElementById('start-screen').style.display = 'none'
  document.getElementById('click-to-resume').style.display = 'flex'
  
  continueButton.addEventListener('click', () => {
    document.getElementById('click-to-resume').style.display = 'none'
    game.loadFromAutoSave(resumableGame)
    showTouchGuideOnce()
  })
  
  newGameButton.addEventListener('click', () => {
    document.getElementById('click-to-resume').style.display = 'none'
    clearStoredGames()
    game.start()
    showTouchGuideOnce()
  })
}

function bindClick(id, handler) {
  const el = document.getElementById(id)
  if (el) el.addEventListener('click', handler)
}

bindClick('start-btn', () => {
  document.getElementById('start-screen').style.display = 'none'
  clearStoredGames()
  game.start()
  showTouchGuideOnce()
})

bindClick('resume-btn', () => game.resume())
bindClick('controller-controls-btn', () => game.showControlsGuide('controller'))
bindClick('touch-inventory-btn', () => game.showFruitMenu())
bindClick('touch-help-btn', () => {
  if (!game.isPaused) game.showControlsGuide('touch')
})
bindClick('controls-guide-close-btn', () => game.closeControlsGuide())
bindClick('touch-pause-btn', () => {
  if (game.isRunning && !game.isPaused) game.pause()
})
bindClick('save-btn', () => game.saveGame())
bindClick('go-home-btn', () => game.goHome())
bindClick('quit-btn', () => game.newGame())
bindClick('death-continue-btn', () => game.respawn())
bindClick('victory-continue-btn', () => game.dismissVictoryScreen())
bindClick('shop-sword-btn', () => game.buyShopUpgrade('sword'))
bindClick('shop-shield-btn', () => game.buyShopUpgrade('shield'))
bindClick('shop-arrow-btn', () => game.buyShopUpgrade('arrow'))
bindClick('shop-potion-btn', () => game.buyShopUpgrade('potion'))
bindClick('shop-apple-btn', () => game.buyShopFruit('apple'))
bindClick('shop-banana-btn', () => game.buyShopFruit('banana'))
bindClick('shop-dragon-fruit-btn', () => game.buyShopFruit('dragonFruit'))
bindClick('shop-golden-pineapple-btn', () => game.buyShopFruit('goldenPineapple'))
bindClick('shop-golden-kiwi-btn', () => game.buyShopFruit('goldenKiwi'))
bindClick('shop-moon-melon-btn', () => game.buyShopFruit('moonMelon'))
bindClick('shop-watermelon-btn', () => game.buyShopFruit('watermelon'))
bindClick('shop-star-fruit-btn', () => game.buyShopFruit('starFruit'))
bindClick('shop-close-btn', () => game.closeUpgradeShop())
bindClick('fruit-health-potion-btn', () => game.eatFruitFromMenu('healthPotion'))
bindClick('fruit-banana-btn', () => game.eatFruitFromMenu('banana'))
bindClick('fruit-apple-btn', () => game.eatFruitFromMenu('apple'))
bindClick('fruit-dragon-fruit-btn', () => game.eatFruitFromMenu('dragonFruit'))
bindClick('fruit-golden-pineapple-btn', () => game.eatFruitFromMenu('goldenPineapple'))
bindClick('fruit-golden-kiwi-btn', () => game.eatFruitFromMenu('goldenKiwi'))
bindClick('fruit-moon-melon-btn', () => game.eatFruitFromMenu('moonMelon'))
bindClick('fruit-watermelon-btn', () => game.eatFruitFromMenu('watermelon'))
bindClick('fruit-star-fruit-btn', () => game.eatFruitFromMenu('starFruit'))
bindClick('fruit-close-btn', () => game.closeFruitMenu())
bindClick('item-popup-ok', () => game.dismissItemPopup())

// Keyboard/gamepad menu navigation for pre-game menus.
let rootMenuButtons = []
let rootMenuIndex = 0
let prevRootPadButtons = []
let prevRootMenuDirection = 0

function getActiveRootMenuButtons() {
  const clickResume = document.getElementById('click-to-resume')
  const startScreen = document.getElementById('start-screen')

  if (clickResume.style.display === 'flex') {
    return ['new-game-btn', 'continue-btn']
      .map(id => document.getElementById(id))
      .filter(button => button && !button.hidden)
  }

  if (startScreen.style.display !== 'none') {
    return ['start-btn']
      .map(id => document.getElementById(id))
      .filter(Boolean)
  }

  return []
}

function updateRootMenuHighlight() {
  const nextButtons = getActiveRootMenuButtons()
  const changed = nextButtons.length !== rootMenuButtons.length || nextButtons.some((b, i) => b !== rootMenuButtons[i])

  if (changed) {
    for (const btn of rootMenuButtons) btn.classList.remove('active')
    rootMenuButtons = nextButtons
    rootMenuIndex = 0
  }

  for (let i = 0; i < rootMenuButtons.length; i++) {
    if (i === rootMenuIndex) rootMenuButtons[i].classList.add('active')
    else rootMenuButtons[i].classList.remove('active')
  }
}

function navigateRootMenu(direction) {
  if (rootMenuButtons.length === 0) return
  rootMenuIndex = (rootMenuIndex + direction + rootMenuButtons.length) % rootMenuButtons.length
  updateRootMenuHighlight()
}

function activateRootMenu() {
  if (rootMenuButtons.length === 0) return
  rootMenuButtons[rootMenuIndex].click()
}

document.addEventListener('keydown', (e) => {
  if (game.isRunning) return
  updateRootMenuHighlight()
  if (rootMenuButtons.length === 0) return

  if (e.code === 'KeyW' || e.code === 'ArrowUp') {
    e.preventDefault()
    navigateRootMenu(-1)
  } else if (e.code === 'KeyS' || e.code === 'ArrowDown') {
    e.preventDefault()
    navigateRootMenu(1)
  } else if (e.code === 'Space' || e.code === 'Enter') {
    e.preventDefault()
    activateRootMenu()
  }
})

function updateRootMenuGamepad() {
  if (game.isRunning) return
  updateRootMenuHighlight()
  if (rootMenuButtons.length === 0) return

  const pads = navigator.getGamepads ? navigator.getGamepads() : []
  const gp = pads ? Array.from(pads).find(Boolean) : null
  if (!gp) {
    prevRootPadButtons = []
    prevRootMenuDirection = 0
    return
  }

  const buttons = gp.buttons.map(b => b.pressed)
  const direction = getGamepadMenuDirection(gp)
  if (direction !== 0 && prevRootMenuDirection === 0) navigateRootMenu(direction)
  if (buttons[0] && !prevRootPadButtons[0]) activateRootMenu()
  prevRootPadButtons = buttons
  prevRootMenuDirection = direction
}

function menuTick() {
  updateRootMenuGamepad()
  requestAnimationFrame(menuTick)
}

menuTick()
